/**
 * One place that owns the AI test-call dialog, so any screen can offer the call.
 *
 * The company card, the brain card, the scanner's scan list and the leads list
 * all offer "AI test call". A menu entry closes its menu the moment it is
 * pressed, and a dialog that lived inside the menu would close with it; a
 * dialog in every list component would be the same dialog four times. So the
 * dialog lives here, once, above every page, and a screen only asks for it.
 *
 * The right is decided here too (`manage:crm.calls`, what the routes demand),
 * so an entry is simply not drawn for somebody the server would refuse.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { userCanPlatform } from "../../../lib/auth";
import { hasTestCallTarget, type AiTestCallTarget } from "../../../lib/aiTestCallPreview";
import { useCrmUser } from "../../shell/CrmUser";
import { AiTestCallDialog } from "./AiTestCallDialog";

interface AiTestCallApi {
  /** The viewer may place AI test calls. Draw no entry when false. */
  canCall: boolean;
  /** Open the dialog for this company. Ignored without a right or a target. */
  open: (target: AiTestCallTarget) => void;
}

const AiTestCallContext = createContext<AiTestCallApi | null>(null);

export function AiTestCallHost({ children }: { children: ReactNode }) {
  const user = useCrmUser();
  const canCall = userCanPlatform(user, "manage:crm.calls");
  const [target, setTarget] = useState<AiTestCallTarget | null>(null);

  const open = useCallback(
    (next: AiTestCallTarget) => {
      if (canCall && hasTestCallTarget(next)) setTarget(next);
    },
    [canCall],
  );
  const value = useMemo(() => ({ canCall, open }), [canCall, open]);

  return (
    <AiTestCallContext.Provider value={value}>
      {children}
      {target ? <AiTestCallDialog target={target} onClose={() => setTarget(null)} /> : null}
    </AiTestCallContext.Provider>
  );
}

/** The dialog's door. Outside the host (a test page), nobody may call and nothing opens. */
export function useAiTestCall(): AiTestCallApi {
  return useContext(AiTestCallContext) ?? { canCall: false, open: () => {} };
}
